/**
 * Miscellaneous Helper Functions
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2019
 */

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import isPlainObject from 'lodash/isPlainObject';

/**
 * Checks between two collections and determines if the contents are equal, optionaly skipping a list of properties
 * @param {Array.<Object>} oldCollection - the original collection to compare with
 * @param {Array.<Object>} newCollection - the new or current collection
 * @param {Array.<String>} [omitProps] - optional array of properties to skip when doing the comparison
 * @returns {Boolean}
 */
export function collectionUnchanged(oldCollection = [], newCollection = [], omitProps = []) {
  let isUnchanged;
  oldCollection.every((obj, index) => {
    isUnchanged = isEqual(omit(obj, omitProps), omit(newCollection[index], omitProps));
    return isUnchanged;
  });
  return isUnchanged;
}

/**
 * Converts a string to array splitting on any commas
 * @param {String} item
 * @returns {Array|Undefined}
 */
export function convertToArray(item) {
  if (Array.isArray(item)) {
    return item;
  }

  let str;
  if (typeof item === 'string') {
    str = item.split(',');
  }
  return str;
}

/**
 * Converts an array to a comma separated string.
 * @param {Array} items
 * @param {String} [key] - key to use to pull out a value if `items` is a collection
 * @returns {String|Undefined}
 */
export function convertToString(items, key = 'value') {
  let arr;
  if (Array.isArray(items) && items.length) {
    if (isPlainObject(items)) {
      arr = items.map((item) => item[key]).join(',');
    } else {
      arr = items.join(',');
    }
  }
  return arr;
}

/**
 * Format a number of bytes into a more human friendly manner
 * @param {Number} number - number of bytes
 * @returns {String} - formatted number
 */
export function fileSize(number) {
  if (number < 1024) {
    return `${number} bytes`;
  } else if (number > 1024 && number < 1048576) {
    return `${(number / 1024).toFixed(1)} KB`;
  } else if (number > 1048576) {
    return `${(number / 1048576).toFixed(1)} MB`;
  }
}

/**
 * Checks if the browser can handle drag and drop uploading
 * @returns {boolean}
 */
export function hasAdvancedUploadSupport() {
  let div = document.createElement('div');
  // are the drag-and-drop events supported
  let canDragDrop = 'draggable' in div || ('ondragstart' in div && 'ondrop' in div);
  // even if they are supported can we access the dropped files
  let canPassDroppedFiles = 'FileReader' in window;
  // Files must be uploaded with the FormData object. Do we have support for that
  let canAjaxUploadFiles = 'FormData' in window;

  // If we can do all 3 then we can have fancy things
  return canDragDrop && canAjaxUploadFiles && canPassDroppedFiles;
}

/**
 * Get the first item that pass the test
 * by second argument function
 *
 * @param {Array} list
 * @param {Function} f
 * @return {*}
 */
export function find(list, f) {
  return list.filter(f)[0];
}

/**
 * Wrapper for lodash/cloneDeep
 * @param {*} item - the item too be cloned
 * @returns {*}
 */
export function deepCopy(item) {
  return cloneDeep(item);
}

/**
 * Clones two different collections and combines them into a new single collection
 * @param {Array.<Object>} originalCollection
 * @param {Array.<Object>} newCollection
 * @param {Number} [startPosition] - Array.splice start position number, defaults to the end
 * @returns {Array.<Object>}
 */
export function combineCollection(originalCollection = [], newCollection = [], startPosition) {
  originalCollection = deepCopy(originalCollection);
  newCollection = deepCopy(newCollection);
  startPosition = typeof startPosition === 'undefined' ? originalCollection.length : startPosition;
  originalCollection.splice(startPosition, 0, ...newCollection);
  return originalCollection;
}

/**
 * Feature detection for the web storage api
 * @param {String} type - 'sessionStorage' or 'localStorage'
 * @returns {Boolean}
 */
export function isStorageAvailable(type) {
  let storage;
  try {
    storage = window[type];
    let x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

/**
 * Returns the visible display name based on the following precedance rules:
 *  1. first name & last name
 *  2. username
 *  3. first name only
 *  4. nothing
 * @param {Object} names
 * @param {string} [names.first_name]
 * @param {string} [names.last_name]
 * @param {string} [names.username]
 * @param {string} [names.email]
 * @returns {string|undefined};
 */
export function getDisplayName(names = {}, firstNameOnly) {
  if (!Object.keys(names).length) {
    return;
  }

  let displayName;
  if (!firstNameOnly && names.first_name && names.last_name) {
    displayName = `${names.first_name} ${names.last_name}`;
  } else if ((firstNameOnly && names.first_name) || (!names.username && names.first_name)) {
    displayName = names.first_name;
  } else if (names.username) {
    displayName = names.username;
  } else {
    displayName = names.email;
  }

  return displayName;
}
